import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { AuthStatusEnum, User } from '@employ-engage/data';
import { GET_COMPLETE_CURRENT_USER } from '../api';
import { doesSessionExist, signOut } from 'supertokens-web-js/recipe/session';
import {
  signIn,
  signUp,
  doesEmailExist,
  sendPasswordResetEmail,
} from 'supertokens-web-js/recipe/emailpassword';
import { useCurrentCompany } from './current-company-context';
import Session from 'supertokens-web-js/recipe/session';
import {
  UserRoleClaim,
  PermissionClaim,
} from 'supertokens-web-js/recipe/userroles';
import { RolesEnum, PermissionsEnum } from '@employ-engage/data';
import { CREATE_PRE_USER_COMPANY } from '../api/pre-user-company';

interface AuthContextType {
  user: User | null;
  status: AuthStatusEnum;
  signin: (email: string, password: string) => Promise<void>;
  signout: () => Promise<void>;
  signup: (
    email: string,
    password: string,
    companyName: string,
    companyId: string
  ) => Promise<void>;
  /** Signing up against an invitation, rather than to start a company. */
  signupWithInvite: (
    email: string,
    password: string,
    inviteToken: string
  ) => Promise<void>;
  resetPassword: (email: string) => Promise<any>;
  checkEmail: (email: string) => Promise<void>;
  refetchUser: () => void;
  updateUser: (updatedUser: Partial<User>) => Promise<void>;
  roles: RolesEnum[];
  permissions: PermissionsEnum[];
  isLoading: boolean;
  hasRole: (role: RolesEnum) => boolean;
  hasPermission: (permission: PermissionsEnum) => boolean;
  RolesEnum: typeof RolesEnum;
  PermissionsEnum: typeof PermissionsEnum;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  status: AuthStatusEnum.Pending,
  signin: async () => {
    return Promise.resolve();
  },
  signout: async () => {
    return Promise.resolve();
  },
  signup: async () => {
    return Promise.resolve();
  },
  signupWithInvite: async () => {
    return Promise.resolve();
  },
  resetPassword: async () => {
    return Promise.resolve();
  },
  checkEmail: async () => {
    return Promise.resolve();
  },
  refetchUser: () => {
    return Promise.resolve();
  },
  updateUser: async (updatedUser: Partial<User>) => {
    return Promise.resolve();
  },
  roles: [] as RolesEnum[],
  permissions: [] as PermissionsEnum[],
  isLoading: true,
  hasRole: (role: RolesEnum) => {
    return false;
  },
  hasPermission: (permission: PermissionsEnum) => {
    return false;
  },
  RolesEnum,
  PermissionsEnum,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatusEnum>(AuthStatusEnum.Pending);
  const { refetchCompany } = useCurrentCompany();
  const client = useApolloClient();
  const [roles, setRoles] = useState<RolesEnum[]>([]);
  const [permissions, setPermissions] = useState<PermissionsEnum[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [createPreUserCompany] = useMutation(CREATE_PRE_USER_COMPANY);

  useEffect(() => {
    if (client) {
      doesSessionExist()
        .then(async (isLoggedIn) => {
          if (!isLoggedIn) {
            console.warn('User is not logged in');
            setStatus(AuthStatusEnum.Unauthenticated);
            setUser(null);
          } else {
            refetchCompany().catch((err) => {
              console.error(
                'Error refetching company after session check',
                err
              );
            });
            const { data } = await client.query({
              query: GET_COMPLETE_CURRENT_USER,
              fetchPolicy: 'network-only',
            });

            if (data.me) {
              setUser(data.me);
              setStatus(AuthStatusEnum.Authenticated);
            } else {
              setStatus(AuthStatusEnum.Unauthenticated);
            }
          }
        })
        .catch((error) => {
          console.error('Error checking session existence:', error);
          if (window.location.pathname !== '/') {
            window.location.pathname = '/';
          }
          setStatus(AuthStatusEnum.Error);
        });
    }
  }, [client, refetchCompany, user?.currentCompanyId]);

  async function signin(email: string, password: string): Promise<void> {
    setIsLoading(true);
    setRoles([]);
    setPermissions([]);
    try {
      const response = await signIn({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: password,
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        const errors = response.formFields.map((formField) => {
          return formField.error;
        });
        throw new Error(`Validation errors: ${errors.join(', ')}`);
      } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        throw new Error('Email password combination is incorrect.');
      } else if (response.status === 'SIGN_IN_NOT_ALLOWED') {
        // the reason string is a user friendly message
        // about what went wrong. It can also contain a support code which users
        // can tell you so you know why their sign in was not allowed.
        throw new Error(response.reason);
      } else {
        // sign in successful. The session tokens are automatically handled by
        // the frontend SDK.

        const { data } = await client.query({
          query: GET_COMPLETE_CURRENT_USER,
          fetchPolicy: 'network-only',
        });

        await refetchCompany();

        if (data.me) {
          setUser(data.me);
          setStatus(AuthStatusEnum.Authenticated);
        } else {
          setStatus(AuthStatusEnum.Unauthenticated);
        }
        return;
      }
    } catch (err: any) {
      if (err.isSuperTokensGeneralError === true) {
        // this may be a custom error message sent from the API by you.
        throw new Error(err.message);
      } else {
        throw new Error(err?.message);
      }
    }
  }

  /**
   * Signing up because you were invited, rather than to start a company.
   *
   * No PreUserCompany is created: the invitation is the authorization, and
   * the company already exists. The token travels as a signup form field so
   * the server can redeem it inside the same call that creates the account —
   * there is no moment in between where a user exists with nowhere to be.
   */
  async function signupWithInvite(
    email: string,
    password: string,
    inviteToken: string
  ): Promise<void> {
    const response = await signUp({
      formFields: [
        { id: 'email', value: email },
        { id: 'password', value: password },
        { id: 'inviteToken', value: inviteToken },
      ],
    });

    if (response.status === 'FIELD_ERROR') {
      throw new Error(
        `Validation errors: ${response.formFields
          .map((formField) => formField.error)
          .join(', ')}`
      );
    }
    if (response.status === 'SIGN_UP_NOT_ALLOWED') {
      throw new Error(response.reason);
    }

    const { data } = await client.query({
      query: GET_COMPLETE_CURRENT_USER,
      fetchPolicy: 'network-only',
    });
    if (data.me) {
      setUser(data.me);
      setStatus(AuthStatusEnum.Authenticated);
    } else {
      setStatus(AuthStatusEnum.Unauthenticated);
    }
  }

  async function signup(
    email: string,
    password: string,
    companyName?: string,
    companyId?: string
  ): Promise<void> {
    try {
      await createPreUserCompany({
        variables: {
          input: {
            companyName: companyName || '',
            companyId: companyId || '',
            ownerEmail: email,
          },
        },
      });

      const response = await signUp({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: password,
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        // one of the input formFields failed validation
        const errors = response.formFields.map((formField) => {
          return formField.error;
        });
        throw new Error(`Validation errors: ${errors.join(', ')}`);
      } else if (response.status === 'SIGN_UP_NOT_ALLOWED') {
        // the reason string is a user friendly message
        // about what went wrong. It can also contain a support code which users
        // can tell you so you know why their sign up was not allowed.
        throw new Error(response.reason);
      } else {
        // sign up successful. The session tokens are automatically handled by
        // the frontend SDK.
        const { data } = await client.query({
          query: GET_COMPLETE_CURRENT_USER,
          fetchPolicy: 'network-only',
        });

        if (data.me) {
          setUser(data.me);
          setStatus(AuthStatusEnum.Authenticated);
        } else {
          setStatus(AuthStatusEnum.Unauthenticated);
        }
        return;
      }
    } catch (err: any) {
      throw new Error(err?.message);
    }
  }

  async function checkEmail(email: string) {
    try {
      const response = await doesEmailExist({
        email,
      });

      if (response.doesExist) {
        throw new Error('Email already exists. Please sign in instead');
      }
    } catch (err: any) {
      throw new Error(err?.message);
    }
  }

  const signout = async () => {
    setStatus(AuthStatusEnum.Pending);
    try {
      await signOut();
      setUser(null);
      setStatus(AuthStatusEnum.Unauthenticated);
      setRoles([]);
      setPermissions([]);
      // It's important to clear the Apollo Client store to remove any cached data
      // that might be specific to the signed-in user.
      await client.clearStore();
    } catch (err: any) {
      setStatus(AuthStatusEnum.Unauthenticated);
      throw err;
    }
  };

  function refetchUser() {
    client
      .query({
        query: GET_COMPLETE_CURRENT_USER,
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (data.me) {
          setUser(data.me);
          setStatus(AuthStatusEnum.Authenticated);
        } else {
          setUser(null);
          setStatus(AuthStatusEnum.Unauthenticated);
        }
      })
      .catch((error) => {
        console.error('Error refetching user:', error);
        setStatus(AuthStatusEnum.Error);
      });
  }

  async function resetPassword(email: string): Promise<any> {
    try {
      return await sendPasswordResetEmail({
        formFields: [{ id: 'email', value: email }],
      });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw new Error(
        'Failed to send password reset email. Please try again later.'
      );
    }
  }

  async function updateUser(updatedUser: any): Promise<void> {
    if (!user) {
      throw new Error('User is not authenticated');
    }
    setUser((previous) => ({ ...previous, ...updatedUser }));
    // Optionally, you can also refetch the user data from the server
    refetchUser();
  }

  async function getUserRolesAndPermissions() {
    // Ensures the payload is up to date (will refresh if needed)
    const payload = await Session.getAccessTokenPayloadSecurely();

    // Extract roles and permissions from the payload using the helpers
    const roles: RolesEnum[] =
      (UserRoleClaim.getValueFromPayload(payload) as RolesEnum[]) || [];
    const permissions: PermissionsEnum[] =
      (PermissionClaim.getValueFromPayload(payload) as PermissionsEnum[]) || [];

    return { roles, permissions };
  }

  useEffect(() => {
    if (isLoading) {
      getUserRolesAndPermissions().then(({ roles, permissions }) => {
        setRoles(roles.map((role) => role as RolesEnum));
        setPermissions(permissions.map((p) => p as PermissionsEnum));
        setIsLoading(false);
      });
    }
  }, [isLoading]);

  const hasRole = (role: RolesEnum) => {
    return roles.includes(role);
  };
  const hasPermission = (permission: PermissionsEnum) => {
    return permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        status,
        signin,
        signout,
        signup,
        signupWithInvite,
        resetPassword,
        checkEmail,
        refetchUser,
        updateUser,
        roles,
        permissions,
        hasRole,
        hasPermission,
        isLoading,
        RolesEnum,
        PermissionsEnum,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthUser = () => useContext(AuthContext);
